/**
 * CapacityTelemetryCard.tsx
 * Feature: 154 - Eventstream Real-Time Capacity Telemetry
 * Author: Peter Dobler
 * Date: 2026-05-18
 * Constitution Version: 1.12.0
 *
 * MUI card showing live CU utilization, throttling %, and capacity state.
 */

import React from 'react';
import { PauseCircle } from 'lucide-react';
import { Card, CardHeader, CardBody, Badge, AppTooltip } from '../ui';
import type { BadgeVariant } from '../ui';
import DataFreshnessIndicator from './DataFreshnessIndicator';
import type { CapacityLiveMetrics } from '@shared/types/capacityTelemetry';

interface CapacityTelemetryCardProps {
  metrics: CapacityLiveMetrics;
  degradedMode: boolean;
}

function cuColor(pct: number | null): string {
  if (pct === null) return 'var(--color-status-success)';
  if (pct < 60) return 'var(--color-status-success)';
  if (pct < 80) return 'var(--color-status-warning)';
  return 'var(--color-status-danger)';
}

const STATE_CHIP: Record<string, { label: string; variant: BadgeVariant }> = {
  active: { label: 'Active', variant: 'success' },
  throttled: { label: 'Throttled', variant: 'warning' },
  overloaded: { label: 'Overloaded', variant: 'error' },
  paused: { label: 'Paused', variant: 'default' },
  resumed: { label: 'Active', variant: 'success' },
  recovered: { label: 'Active', variant: 'success' },
};

export default function CapacityTelemetryCard({
  metrics,
  degradedMode,
}: CapacityTelemetryCardProps) {
  const isPaused = metrics.currentState === 'paused';
  const stateChip = STATE_CHIP[metrics.currentState ?? 'active'] ?? STATE_CHIP.active;
  const cuPct = metrics.cuUtilizationPct;

  return (
    <Card variant="outlined" className="h-full">
      <CardHeader className="mb-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-base font-semibold text-[var(--color-text-primary)]">
            {metrics.capacityName}
          </p>
          <div className="mt-0.5 flex items-center justify-between">
            <span className="text-xs text-[var(--color-text-secondary)]">
              {metrics.capacityType}
            </span>
            <div className="flex items-center gap-1">
              <Badge variant={stateChip.variant}>{stateChip.label}</Badge>
              {!metrics.isStreaming && (
                <AppTooltip title="Polling-based data (streaming not active)">
                  <Badge variant="default" isOutlined>
                    Polling
                  </Badge>
                </AppTooltip>
              )}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardBody>
        {isPaused ? (
          <div className="my-2 flex items-center gap-2 text-[var(--color-text-secondary)]">
            <PauseCircle className="h-4 w-4" />
            <span className="text-sm">Paused — no data</span>
          </div>
        ) : (
          <>
            {/* CU utilization */}
            <div className="mb-3">
              <div className="mb-0.5 flex justify-between">
                <span className="text-xs text-[var(--color-text-secondary)]">CU Utilization</span>
                <span className="text-xs font-semibold text-[var(--color-text-primary)]">
                  {cuPct !== null ? `${cuPct.toFixed(1)}%` : '—'}
                </span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-[var(--color-bg-subtle)]">
                <div
                  className="h-full rounded-full transition-[width] duration-300"
                  style={{
                    width: `${Math.min(cuPct ?? 0, 100)}%`,
                    backgroundColor: cuColor(cuPct),
                  }}
                />
              </div>
            </div>

            {/* Throttling */}
            {metrics.interactiveDelayThrottlingPct !== undefined &&
              metrics.interactiveDelayThrottlingPct !== null &&
              metrics.interactiveDelayThrottlingPct > 0 && (
                <div className="mb-2">
                  <span className="text-xs text-[var(--color-text-secondary)]">
                    Interactive Delay Throttling:{' '}
                    <strong>{metrics.interactiveDelayThrottlingPct.toFixed(1)}%</strong>
                  </span>
                </div>
              )}
          </>
        )}

        <DataFreshnessIndicator
          lastEventAt={metrics.lastEventAt ?? null}
          degradedMode={degradedMode}
          compact
        />
      </CardBody>
    </Card>
  );
}
